import { Handle, Position, useStoreApi } from '@xyflow/react';
import type { MouseEvent } from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import BlockSelector from '../../components/block-selector';
import { useAvailableBlocks } from '../../hooks/use-nodes-data';
import { useNodesInteractions } from '../../hooks/use-nodes-interactions';
import { useStore } from '../../store';
import { Node, NodeEnum } from '../../types';
import { useWorkflowReadOnly } from '../../hooks';

interface NodeHandleProps extends Pick<Node, 'id' | 'data'> {
  handleId: string;
  handleClassName?: string;
  nodeSelectorClassName?: string;
}

export const NodeTargetHandle = memo(
  ({
    id,
    data,
    handleId,
    handleClassName,
    nodeSelectorClassName,
  }: NodeHandleProps) => {
    const [open, setOpen] = useState(false);
    const { handleNodeAdd } = useNodesInteractions();
    const notInitialWorkflow = useStore((s) => s.notInitialWorkflow);
    const { nodesReadOnly } = useWorkflowReadOnly();
    const connected = data._connectedTargetHandleIds?.includes(handleId);
    const { availablePrevBlocks } = useAvailableBlocks(data.type);
    const isConnectable = !!availablePrevBlocks.length;

    const handleOpenChange = useCallback((v: boolean) => {
      setOpen(v);
    }, []);
    const handleHandleClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        if (!connected) setOpen((v) => !v);
      },
      [connected],
    );
    const handleSelect = useCallback(
      (type: NodeEnum) => {
        handleNodeAdd(
          {
            nodeType: type,
          },
          {
            nextNodeId: id,
            nextNodeTargetHandle: handleId,
          },
        );
      },
      [handleNodeAdd, id, handleId],
    );

    return (
      <>
        <Handle
          id={handleId}
          type="target"
          position={Position.Left}
          className={`
          !w-4 !h-4 !bg-transparent !rounded-none !outline-none !border-none z-[1]
          after:absolute after:w-0.5 after:h-3 after:top-[2px] after:right-2  after:bg-blue-500
          hover:scale-125 transition-all
          ${!connected && 'after:opacity-0'}
          group-hover:after:opacity-100
          ${data.type === NodeEnum.Start && 'opacity-0'}
          ${handleClassName}
        `}
          onClick={handleHandleClick}
        >
          {!connected && isConnectable && !nodesReadOnly && (
            <BlockSelector
              open={open}
              onOpenChange={handleOpenChange}
              onSelect={handleSelect}
              asChild
              placement="left"
              triggerClassName={(open) => `
                hidden absolute left-0 top-0 pointer-events-none
                ${nodeSelectorClassName}
                group-hover:!flex
                ${data._selected && '!flex'}
                ${open && '!flex'}
              `}
              availableBlocksTypes={availablePrevBlocks}
            />
          )}
        </Handle>
      </>
    );
  },
);
NodeTargetHandle.displayName = 'NodeTargetHandle';

export const NodeSourceHandle = memo(
  ({
    id,
    data,
    handleId,
    handleClassName,
    nodeSelectorClassName,
  }: NodeHandleProps) => {
    const notInitialWorkflow = useStore((s) => s.notInitialWorkflow);
    // const mode = useStore((s) => s.mode);
    const [open, setOpen] = useState(false);
    const { handleNodeAdd } = useNodesInteractions();

    const { nodesReadOnly } = useWorkflowReadOnly();
    const { availableNextBlocks } = useAvailableBlocks(data.type);
    const store = useStoreApi();

    const isConnectable = !!availableNextBlocks.length;

    // 如果当前节点的source handler 包括当前handleId，则表示当前节点已经连接
    const connected = data._connectedSourceHandleIds?.includes(handleId);

    const handleOpenChange = useCallback((v: boolean) => {
      setOpen(v);
    }, []);
    const handleHandleClick = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        setOpen((v) => !v);
      },
      [connected],
    );
    const handleSelect = useCallback(
      (type: NodeEnum) => {
        handleNodeAdd(
          {
            nodeType: type,
          },
          {
            prevNodeId: id,
            prevNodeSourceHandle: handleId,
          },
        );
      },
      [handleNodeAdd, id, handleId],
    );

    useEffect(() => {
      const nodes = store.getState().nodes;
      if (
        data.type === NodeEnum.Start &&
        nodes.length === 1 &&
        !nodesReadOnly
      ) {
        setOpen(true);
      }
    }, [store, nodesReadOnly, data]);

    return (
      <>
        <Handle
          id={handleId}
          type="source"
          position={Position.Right}
          className={`
          !w-4 !h-4 !bg-transparent !rounded-none !outline-none !border-none z-[1]
          after:absolute after:w-0.5 after:h-3 after:right-2 after:top-[2px] after:bg-blue-500
          hover:scale-125 transition-all
          ${!connected && 'after:opacity-0'}
          group-hover:after:opacity-0
          ${handleClassName}
        `}
          isConnectable={isConnectable}
          onClick={handleHandleClick}
        >
          {isConnectable && !nodesReadOnly && (
            <BlockSelector
              open={open}
              onOpenChange={handleOpenChange}
              onSelect={handleSelect}
              asChild
              triggerClassName={(open) => `
                hidden absolute top-0 left-0 pointer-events-none
                ${nodeSelectorClassName}
                group-hover:!flex
                ${data._selected && '!flex'}
                ${open && '!flex'}
              `}
              availableBlocksTypes={availableNextBlocks}
            />
          )}
        </Handle>
      </>
    );
  },
);
NodeSourceHandle.displayName = 'NodeSourceHandle';
